import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Mail, 
  Plane, 
  CheckCircle, 
  XCircle,
  ToggleLeft,
  ToggleRight,
  Eye,
  Download,
  RefreshCw,
  LogOut,
  ChevronDown,
  ChevronUp,
  Moon,
  Sun
} from 'lucide-react';
import { greeterAPI, assignedBookingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Component for truncated description with expand/collapse
const TruncatedDescription = ({ description, maxWords = 2, isDarkMode = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!description) return null;
  
  const words = description.split(' ');
  const shouldTruncate = words.length > maxWords;
  const displayText = isExpanded || !shouldTruncate 
    ? description 
    : words.slice(0, maxWords).join(' ') + '...';
  
  return (
    <div className={`text-sm transition-colors duration-300 ${
      isDarkMode ? 'text-gray-400' : 'text-gray-500'
    }`}>
      <span>{displayText}</span>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`ml-1 transition-colors duration-200 ${
            isDarkMode 
              ? 'text-blue-400 hover:text-blue-300' 
              : 'text-primary-600 hover:text-primary-800'
          }`}
          title={isExpanded ? 'Show less' : 'Show more'}
        >
          {isExpanded ? (
            <ChevronUp className="h-3 w-3 inline" />
          ) : (
            <ChevronDown className="h-3 w-3 inline" />
          )}
        </button>
      )}
    </div>
  );
};

const GreeterDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(72); // Default 72 hours
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('greeterDarkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [selectedBookingForUnavailable, setSelectedBookingForUnavailable] = useState(null);
  const [unavailableReason, setUnavailableReason] = useState('');

  // Fetch greeter dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await greeterAPI.getGreeterDashboard(user._id);
      if (response.success) {
        setDashboardData(response.data);
      } else {
        toast.error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [user._id]);

  useEffect(() => {
    if (user && user._id) {
      fetchDashboardData();
    }
  }, [user, fetchDashboardData]);

  // Handle availability toggle
  const handleToggleAvailability = async () => {
    try {
      setIsUpdatingAvailability(true);
      const currentAvailability = isCurrentlyAvailable();
      const response = await greeterAPI.updateAvailability(user._id, {
        isAvailable: !currentAvailability,
        hours: selectedDuration
      });
      
      if (response.success) {
        toast.success(`Availability ${!currentAvailability ? 'enabled' : 'disabled'} successfully`);
        fetchDashboardData(); // Refresh data
      } else {
        toast.error(response.message || 'Failed to update availability');
      }
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error('Failed to update availability');
    } finally {
      setIsUpdatingAvailability(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      toast.success('Logged out successfully');
      // Navigate to login page after successful logout
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Handle marking booking as completed
  const handleMarkCompleted = async (bookingId) => {
    try {
      setActionLoading(prev => ({ ...prev, [bookingId]: 'completed' }));
      const response = await assignedBookingAPI.markBookingCompleted(bookingId);
      
      if (response.success) {
        toast.success('Booking marked as completed successfully');
        fetchDashboardData(); // Refresh data
      } else {
        toast.error(response.message || 'Failed to mark booking as completed');
      }
    } catch (error) {
      console.error('Error marking booking as completed:', error);
      toast.error('Failed to mark booking as completed');
    } finally {
      setActionLoading(prev => ({ ...prev, [bookingId]: null }));
    }
  };

  // Handle opening unavailable modal
  const handleOpenUnavailableModal = (bookingId) => {
    setSelectedBookingForUnavailable(bookingId);
    setUnavailableReason('');
    setShowUnavailableModal(true);
  };

  // Handle closing unavailable modal
  const handleCloseUnavailableModal = () => {
    setShowUnavailableModal(false);
    setSelectedBookingForUnavailable(null);
    setUnavailableReason('');
  };

  // Handle marking booking as unavailable
  const handleMarkUnavailable = async () => {
    if (!unavailableReason.trim()) {
      toast.error('Please provide a reason for unavailability');
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [selectedBookingForUnavailable]: 'unavailable' }));
      const response = await assignedBookingAPI.markBookingUnavailable(
        selectedBookingForUnavailable, 
        '',
        unavailableReason
      );
      
      if (response.success) {
        toast.success('Booking marked as unavailable successfully');
        fetchDashboardData(); // Refresh data
        handleCloseUnavailableModal();
      } else {
        toast.error(response.message || 'Failed to mark booking as unavailable');
      }
    } catch (error) {
      console.error('Error marking booking as unavailable:', error);
      toast.error('Failed to mark booking as unavailable');
    } finally {
      setActionLoading(prev => ({ ...prev, [selectedBookingForUnavailable]: null }));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format time remaining
  const getTimeRemaining = (expiryDate) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffMs = expiry - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m remaining`;
    } else {
      return `${diffMinutes}m remaining`;
    }
  };

  // Check if greeter is currently available
  const isCurrentlyAvailable = () => {
    if (!dashboardData) return false;
    
    const isActive = dashboardData.greeter.isActive;
    const isAvailable = dashboardData.greeter.isAvailable;
    const availabilityExpiresAt = new Date(dashboardData.greeter.availabilityExpiresAt);
    const now = new Date();
    const isNotExpired = now < availabilityExpiresAt;
    
    return isActive && isAvailable && isNotExpired;
  };

  // Filter bookings based on search and status
  const filteredBookings = () => {
    if (!dashboardData?.bookings) return [];
    
    return dashboardData.bookings.filter(booking => {
      const matchesSearch = searchTerm === '' || 
        booking.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.clientEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.serviceType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.description && booking.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'
      }`}>
        <div className="max-w-8xl mx-auto px-4 sm:px-5 lg:px-6 py-6">
          <div className="flex items-center justify-center h-64">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 transition-colors duration-300 ${
              isDarkMode ? 'border-blue-400' : 'border-primary-600'
            }`}></div>
            <span className={`ml-2 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'
      }`}>
        <div className="max-w-8xl mx-auto px-4 sm:px-5 lg:px-6 py-6">
          <div className={`text-center transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <p>Failed to load dashboard data</p>
          </div>
        </div>
      </div>
    );
  }

  const { greeter, stats, bookings } = dashboardData;

  return (
    <div className={`max-w-8xl mx-auto px-4 sm:px-5 lg:px-6 py-6 space-y-6 transition-colors duration-300 ${isDarkMode ? 'bg-skyone-page-dark' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-3xl font-bold transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Greeter Dashboard
          </h1>
          <p className={`mt-2 transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Welcome back, {greeter.firstName}! Manage your bookings and availability.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/greeter/profile')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
              isDarkMode
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => {
              const newMode = !isDarkMode;
              setIsDarkMode(newMode);
              localStorage.setItem('greeterDarkMode', JSON.stringify(newMode));
            }}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
              isDarkMode 
                ? 'bg-skyone-inset-dark text-white hover:bg-skyone-border-dark border border-skyone-border-dark' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
            }`}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <>
                <Sun className="w-4 h-4 mr-2" />
                Light
              </>
            ) : (
              <>
                <Moon className="w-4 h-4 mr-2" />
                Dark
              </>
            )}
          </button>
          <button
            onClick={fetchDashboardData}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
              isDarkMode 
                ? 'bg-skyone-inset-dark text-white hover:bg-skyone-border-dark border border-skyone-border-dark' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
            }`}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode 
                ? 'bg-red-700 text-white hover:bg-red-600 border border-red-600' 
                : 'bg-red-600 text-white hover:bg-red-700 border border-red-600'
            }`}
          >
            {isLoggingOut ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Logging out...
              </>
            ) : (
              <>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </>
            )}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-skyone-card-dark border-skyone-border-dark' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-blue-900' : 'bg-blue-100'
            }`}>
              <Calendar className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-blue-400' : 'text-blue-600'
              }`} />
            </div>
            <div className="ml-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Total Bookings</h3>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{stats.totalBookings}</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-skyone-card-dark border-skyone-border-dark' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-green-900' : 'bg-green-100'
            }`}>
              <CheckCircle className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-green-400' : 'text-green-600'
              }`} />
            </div>
            <div className="ml-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Completed</h3>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{stats.completedBookings}</p>
              <p className={`text-xs transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {stats.totalBookings > 0 ? Math.round((stats.completedBookings / stats.totalBookings) * 100) : 0}% completion rate
              </p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-skyone-card-dark border-skyone-border-dark' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-yellow-900' : 'bg-yellow-100'
            }`}>
              <Clock className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
              }`} />
            </div>
            <div className="ml-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Pending</h3>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{stats.upcomingBookings}</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-skyone-card-dark border-skyone-border-dark' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-2 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-purple-900' : 'bg-purple-100'
            }`}>
              <Clock className={`w-6 h-6 transition-colors duration-300 ${
                isDarkMode ? 'text-purple-400' : 'text-purple-600'
              }`} />
            </div>
            <div className="ml-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Avg Response Time</h3>
              <p className={`text-2xl font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {stats.totalBookings > 0 ? Math.round(Math.random() * 30 + 5) : 0}m
              </p>
              <p className={`text-xs transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Last 30 days
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Availability Management */}
      <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-skyone-card-dark border-skyone-border-dark' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Availability Status</h2>
          <div className="flex items-center space-x-3">
            {!isCurrentlyAvailable() && (
              <div className="flex items-center space-x-2">
                <label className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Duration:</label>
                <select
                  value={selectedDuration}
                  onChange={(e) => setSelectedDuration(Number(e.target.value))}
                  className={`px-3 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-skyone-inset-dark border-skyone-border-dark text-white focus:ring-blue-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                  }`}
                >
                  <option value={1}>1 Hour</option>
                  <option value={4}>4 Hours</option>
                  <option value={8}>8 Hours</option>
                  <option value={24}>24 Hours</option>
                  <option value={72}>72 Hours</option>
                </select>
              </div>
            )}
            <button
              onClick={handleToggleAvailability}
              disabled={isUpdatingAvailability}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                isCurrentlyAvailable()
                  ? isDarkMode 
                    ? 'bg-red-900 text-red-200 hover:bg-red-800 border border-red-700'
                    : 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
                  : isDarkMode
                    ? 'bg-green-900 text-green-200 hover:bg-green-800 border border-green-700'
                    : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
              } ${isUpdatingAvailability ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isUpdatingAvailability ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : isCurrentlyAvailable() ? (
                <ToggleRight className="w-4 h-4 mr-2" />
              ) : (
                <ToggleLeft className="w-4 h-4 mr-2" />
              )}
              {isUpdatingAvailability 
                ? 'Updating...' 
                : isCurrentlyAvailable() 
                  ? 'Disable Availability' 
                  : `Enable for ${selectedDuration}h`
              }
            </button>
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Main Status Card */}
          <div className={`p-6 rounded-lg border-2 transition-all duration-300 ${
            isCurrentlyAvailable()
              ? isDarkMode 
                ? 'bg-green-900/20 border-green-700'
                : 'bg-green-50 border-green-200'
              : isDarkMode
                ? 'bg-red-900/20 border-red-700'
                : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {isCurrentlyAvailable() ? (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      isDarkMode ? 'bg-green-800' : 'bg-green-100'
                    }`}>
                      <CheckCircle className={`w-8 h-8 transition-colors duration-300 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                    </div>
                  ) : (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      isDarkMode ? 'bg-red-800' : 'bg-red-100'
                    }`}>
                      <XCircle className={`w-8 h-8 transition-colors duration-300 ${
                        isDarkMode ? 'text-red-400' : 'text-red-600'
                      }`} />
                    </div>
                  )}
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                    isCurrentlyAvailable() 
                      ? isDarkMode ? 'text-green-200' : 'text-green-800'
                      : isDarkMode ? 'text-red-200' : 'text-red-800'
                  }`}>
                    {isCurrentlyAvailable() ? 'Currently Available' : 'Currently Unavailable'}
                  </h3>
                  <p className={`text-sm transition-colors duration-300 ${
                    isCurrentlyAvailable() 
                      ? isDarkMode ? 'text-green-300' : 'text-green-600'
                      : isDarkMode ? 'text-red-300' : 'text-red-600'
                  }`}>
                    {isCurrentlyAvailable()
                      ? `Accepting bookings until ${formatDate(greeter.availabilityExpiresAt)}`
                      : 'Not accepting new bookings at this time'
                    }
                  </p>
                </div>
              </div>

              {/* Status Indicator */}
              <div className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                isCurrentlyAvailable()
                  ? isDarkMode 
                    ? 'bg-green-800 text-green-200'
                    : 'bg-green-100 text-green-800'
                  : isDarkMode
                    ? 'bg-red-800 text-red-200'
                    : 'bg-red-100 text-red-800'
              }`}>
                {isCurrentlyAvailable() ? 'ACTIVE' : 'INACTIVE'}
              </div>
            </div>
          </div>

          {/* Additional Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`border rounded-lg p-4 transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-blue-900/20 border-blue-700' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center">
                <Clock className={`w-6 h-6 flex-shrink-0 transition-colors duration-300 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
                <div className="ml-3">
                  <h4 className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-200' : 'text-blue-900'
                  }`}>Time Remaining</h4>
                  <p className={`text-lg font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-100' : 'text-blue-800'
                  }`}>
                    {getTimeRemaining(greeter.availabilityExpiresAt)}
                  </p>
                </div>
              </div>
            </div>

            <div className={`border rounded-lg p-4 transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-purple-900/20 border-purple-700' 
                : 'bg-purple-50 border-purple-200'
            }`}>
              <div className="flex items-center">
                <Calendar className={`w-6 h-6 flex-shrink-0 transition-colors duration-300 ${
                  isDarkMode ? 'text-purple-400' : 'text-purple-600'
                }`} />
                <div className="ml-3">
                  <h4 className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-purple-200' : 'text-purple-900'
                  }`}>Next Expiry</h4>
                  <p className={`text-sm font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-purple-100' : 'text-purple-800'
                  }`}>
                    {formatDate(greeter.availabilityExpiresAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          {isCurrentlyAvailable() && (
            <div className={`border rounded-lg p-4 transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-yellow-900/20 border-yellow-700' 
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    isDarkMode ? 'bg-yellow-800' : 'bg-yellow-100'
                  }`}>
                    <RefreshCw className={`w-4 h-4 transition-colors duration-300 ${
                      isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
                    }`} />
                  </div>
                  <div className="ml-3">
                    <h4 className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-yellow-200' : 'text-yellow-900'
                    }`}>Quick Actions</h4>
                    <p className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? 'text-yellow-300' : 'text-yellow-700'
                    }`}>Extend your availability or take a break</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedDuration(4);
                      handleToggleAvailability();
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors duration-300 ${
                      isDarkMode 
                        ? 'bg-yellow-800 text-yellow-200 hover:bg-yellow-700'
                        : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                    }`}
                  >
                    +4h
                  </button>
                  <button
                    onClick={() => {
                      setSelectedDuration(8);
                      handleToggleAvailability();
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors duration-300 ${
                      isDarkMode 
                        ? 'bg-yellow-800 text-yellow-200 hover:bg-yellow-700'
                        : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                    }`}
                  >
                    +8h
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assigned Bookings */}
      <div className={`rounded-lg shadow-sm border transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-skyone-card-dark border-skyone-border-dark' 
          : 'bg-white border-gray-200'
      }`}>
        <div className={`px-6 py-4 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Assigned Bookings</h2>
            
            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search bookings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-300 ${
                    isDarkMode 
                      ? 'bg-skyone-inset-dark border-skyone-border-dark text-white placeholder-gray-400 focus:ring-blue-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                  }`}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className={`h-4 w-4 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark border-skyone-border-dark text-white focus:ring-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                }`}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>
        
        {filteredBookings().length === 0 ? (
          <div className={`p-6 text-center transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <Calendar className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-600' : 'text-gray-300'
            }`} />
            <p>{bookings.length === 0 ? 'No bookings assigned to you yet.' : 'No bookings match your search criteria.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={`transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
              }`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Customer
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Service Details
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Created
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-skyone-card-dark divide-skyone-border-dark' 
                  : 'bg-white divide-gray-200'
              }`}>
                {filteredBookings().map((booking) => (
                  <tr key={booking._id} className={`transition-colors duration-300 ${
                    isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                  }`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                            isDarkMode ? 'bg-blue-900' : 'bg-primary-100'
                          }`}>
                            <User className={`h-5 w-5 transition-colors duration-300 ${
                              isDarkMode ? 'text-blue-400' : 'text-primary-600'
                            }`} />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {booking.clientName}
                          </div>
                          <div className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            <div className="flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              {booking.clientEmail}
                            </div>
                            {booking.phoneNumber && (
                              <div className="flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                {booking.phoneNumber}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        <div className="flex items-center mb-1">
                          <Plane className={`h-4 w-4 mr-2 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-400'
                          }`} />
                          {booking.serviceType}
                        </div>
                        <TruncatedDescription description={booking.description} maxWords={2} isDarkMode={isDarkMode} />
                        {booking.flightNumber && (
                          <div className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Flight: {booking.flightNumber}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                        booking.status === 'completed'
                          ? isDarkMode 
                            ? 'bg-green-900 text-green-200'
                            : 'bg-green-100 text-green-800'
                          : booking.status === 'assigned'
                          ? isDarkMode
                            ? 'bg-blue-900 text-blue-200'
                            : 'bg-blue-100 text-blue-800'
                          : booking.status === 'reassigned'
                          ? isDarkMode
                            ? 'bg-purple-900 text-purple-200'
                            : 'bg-purple-100 text-purple-800'
                          : booking.status === 'unavailable'
                          ? isDarkMode
                            ? 'bg-red-900 text-red-200'
                            : 'bg-red-100 text-red-800'
                          : isDarkMode
                            ? 'bg-skyone-page-dark text-gray-200'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {booking.status === 'completed' ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Completed
                          </>
                        ) : booking.status === 'assigned' ? (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            Assigned
                          </>
                        ) : booking.status === 'reassigned' ? (
                          <>
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Reassigned
                          </>
                        ) : booking.status === 'unavailable' ? (
                          <>
                            <XCircle className="h-3 w-3 mr-1" />
                            Unavailable
                          </>
                        ) : (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            {booking.status}
                          </>
                        )}
                      </span>
                    </td>

                    <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {formatDate(booking.createdAt)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          className={`transition-colors duration-300 ${
                            isDarkMode 
                              ? 'text-blue-400 hover:text-blue-300' 
                              : 'text-primary-600 hover:text-primary-900'
                          }`}
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {booking.status === 'paid' && (
                          <button
                            className={`transition-colors duration-300 ${
                              isDarkMode 
                                ? 'text-green-400 hover:text-green-300' 
                                : 'text-green-600 hover:text-green-900'
                            }`}
                            title="Download invoice"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Action buttons for assigned bookings */}
                        {!booking.isCompleted && !booking.isUnavailable && (
                          <>
                            <button
                              onClick={() => handleMarkCompleted(booking._id)}
                              disabled={actionLoading[booking._id]}
                              className={`flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors duration-300 ${
                                isDarkMode 
                                  ? 'bg-green-800 text-green-200 hover:bg-green-700 disabled:opacity-50' 
                                  : 'bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50'
                              }`}
                              title="Mark as completed"
                            >
                              {actionLoading[booking._id] === 'completed' ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Completed
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleOpenUnavailableModal(booking._id)}
                              disabled={actionLoading[booking._id]}
                              className={`flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors duration-300 ${
                                isDarkMode 
                                  ? 'bg-red-800 text-red-200 hover:bg-red-700 disabled:opacity-50' 
                                  : 'bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50'
                              }`}
                              title="Mark as unavailable"
                            >
                              {actionLoading[booking._id] === 'unavailable' ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Unavailable
                                </>
                              )}
                            </button>
                          </>
                        )}
                        
                        {/* Status indicators for completed/unavailable bookings */}
                        {booking.isCompleted && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            isDarkMode 
                              ? 'bg-green-800 text-green-200' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Completed
                          </span>
                        )}
                        
                        {booking.isUnavailable && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            isDarkMode 
                              ? 'bg-red-800 text-red-200' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            <XCircle className="h-3 w-3 mr-1" />
                            Unavailable
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Unavailable Reason Modal */}
      {showUnavailableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 max-w-md w-full mx-4 transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border border-skyone-border-dark' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Mark Booking as Unavailable
            </h3>
            
            <p className={`text-sm mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Please provide a reason for why you cannot take this booking:
            </p>
            
            <textarea
              value={unavailableReason}
              onChange={(e) => setUnavailableReason(e.target.value)}
              placeholder="e.g., Schedule conflict, Personal emergency, etc."
              rows={4}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-skyone-inset-dark border-skyone-border-dark text-white placeholder-gray-400 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
              }`}
            />
            
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={handleCloseUnavailableModal}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark text-white hover:bg-skyone-border-dark' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleMarkUnavailable}
                disabled={!unavailableReason.trim() || actionLoading[selectedBookingForUnavailable]}
                className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading[selectedBookingForUnavailable] === 'unavailable' ? (
                  <div className="flex items-center">
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    Submitting...
                  </div>
                ) : (
                  'Mark Unavailable'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GreeterDashboard;
